import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {MutationObserver | null} */
  #mutationObserver = null;
  #marqueeWidth = 0;

  connectedCallback() {
    super.connectedCallback();

    const { content } = this.refs;
    if (!content) return;

    // Use ResizeObserver to detect when the container resizes
    this.#resizeObserver = new ResizeObserver(debounce(() => this.#update(), 100));
    this.#resizeObserver.observe(this);

    // Use MutationObserver to detect when items are added/removed (Theme Editor support)
    this.#mutationObserver = new MutationObserver(debounce(() => this.#update(), 100));
    this.#mutationObserver.observe(content, { childList: true, subtree: true });

    // Only add hover listeners if pause-on-hover is enabled and it's not a touch device
    if (this.getAttribute('data-pause-on-hover') === 'true' && !window.matchMedia('(pointer: coarse)').matches) {
      this.addEventListener('pointerenter', this.#slowDown);
      this.addEventListener('pointerleave', this.#speedUp);
    }

    // Initial check
    this.#update();
  }

  #update() {
    const { content, wrapper, marqueeItems } = this.refs;
    if (!content || !wrapper) return;

    const marqueeWidth = this.clientWidth;
    // Important: scrollWidth of content works because we added width: max-content in Liquid
    const totalContentWidth = content.scrollWidth;

    this.#marqueeWidth = marqueeWidth;

    // We add a 20px buffer to ensure it only rotates when it clearly overflows
    const isDisabled = marqueeWidth <= 0 || totalContentWidth <= marqueeWidth + 20;

    if (isDisabled) {
      this.setAttribute('data-disabled', 'true');
      this.clonedContent?.remove();
    } else {
      this.setAttribute('data-disabled', 'false');
      
      // Calculate how many clones we need to fill the gap (usually 1 is enough for standard marquee)
      const itemWidth = marqueeItems[0]?.offsetWidth || 350;
      const numberOfCopies = itemWidth === 0 ? 1 : Math.ceil(marqueeWidth / itemWidth);
      
      const speed = this.#calculateSpeed(numberOfCopies);
      this.#setSpeed(speed);
      
      // Ensure we have a clone
      if (!this.clonedContent) {
        this.#duplicateContent();
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  /**
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const speed = Math.sqrt(numberOfCopies) * speedFactor;

    return speed;
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }
}
// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
